import React from 'react';
import { Form } from 'react-bootstrap';
import { ApplicantState, InputFieldState } from '../types';
import IdentificationNumberForm from '../IdentificationNumberForm';
import PhoneNumberForm from '../PhoneNumberForm';
import AddressForm from '../AddressForm';
import NameForm from '../NameForm';
import RepresentativeForm from '../RepresentativeForm';
import PaymentForm from '../PaymentForm';

/**
 * Applicantコンポーネント用のPropsです。
 * @interface ApplicantProps
 */
interface Props {

  /**
   * 申出人
   * @type {string}
   * @memberof ApplicantProps
   */
  applicant: ApplicantState;

  /**
   * 申出人を設定する関数
   * @memberof ApplicantProps
   */
  onChange: (updatedApplicant: ApplicantState) => void;

}

/**
 * 出願人フォームのコンポーネントです。
 * @param {function} props.onChange - 出願人が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 出願人フォームのJSX要素
 */
const ApplicantForm: React.FC<Props> = ({ applicant, onChange }) => {

  /**
   * 識別番号を更新する関数です。
   * @param {string} identificationNumber - 識別番号
   * @returns {void}
   */
  const updateIdentificationNumber = (identificationNumber: InputFieldState): void => {
    onChange(
      {
        identificationNumber: {
          value: identificationNumber.value,
          errorMessage: identificationNumber.errorMessage
        },
        address: applicant.address,
        name: applicant.name,
        representative: applicant.representative,
        phoneNumber: applicant.phoneNumber,
        payment: {
          method: applicant.payment.method,
          paymentNumber: applicant.payment.paymentNumber
        }
      }
    );
  };

  /**
   * 住所又は居所を更新する関数です。
   * @param {InputFieldState} address - 住所又は居所
   * @returns {void}
   */
  const updateAddress = (address: InputFieldState): void => {
    onChange(
      {
        identificationNumber: applicant.identificationNumber,
        address: address,
        name: applicant.name,
        representative: applicant.representative,
        phoneNumber: applicant.phoneNumber,
        payment: {
          method: applicant.payment.method,
          paymentNumber: applicant.payment.paymentNumber
        }
      }
    );
  };

  /**
   * 氏名又は名称を更新する関数です。
   * @param {InputFieldState} name - 氏名又は名称
   * @returns {void}
   */
  const updateName = (name: InputFieldState): void => {
    onChange(
      {
        identificationNumber: applicant.identificationNumber,
        address: applicant.address,
        name: name,
        representative: applicant.representative,
        phoneNumber: applicant.phoneNumber,
        payment: {
          method: applicant.payment.method,
          paymentNumber: applicant.payment.paymentNumber
        }
      }
    );
  };

  /**
   * 代表者を更新する関数です。
   * @param {InputFieldState} representative - 代表者
   * @returns {void}
   */
  const updateRepresentative = (representative: InputFieldState): void => {
    onChange(
      {
        identificationNumber: applicant.identificationNumber,
        address: applicant.address,
        name: applicant.name,
        representative: representative,
        phoneNumber: applicant.phoneNumber,
        payment: {
          method: applicant.payment.method,
          paymentNumber: applicant.payment.paymentNumber
        }
      }
    );
  };

  /**
   * 電話番号を更新する関数です。
   * @param {InputFieldState} phoneNumber - 電話番号
   * @returns {void}
   */
  const updatephoneNumber = (phoneNumber: InputFieldState): void => {
    onChange(
      {
        identificationNumber: applicant.identificationNumber,
        address: applicant.address,
        name: applicant.name,
        representative: applicant.representative,
        phoneNumber: phoneNumber,
        payment: {
          method: applicant.payment.method,
          paymentNumber: applicant.payment.paymentNumber
        }
      }
    );
  };

  /**
   * 納付方法を更新する関数です。
   * @param {InputFieldState} paymentMethod - 納付方法
   * @returns {void}
   */
  const updatePaymentMethod = (paymentMethod: InputFieldState): void => {
    onChange(
      {
        identificationNumber: applicant.identificationNumber,
        address: applicant.address,
        name: applicant.name,
        representative: applicant.representative,
        phoneNumber: applicant.phoneNumber,
        payment: {
          method: paymentMethod,
          paymentNumber: {
            value: '',
            errorMessage: ''
          }
        }
      }
    );
  };

  /**
   * 納付番号を更新する関数です。
   * @param {InputFieldState} paymentNumber - 納付番号
   * @returns {void}
   */
  const updatePaymentNumber = (paymentNumber: InputFieldState): void => {
    onChange(
      {
        identificationNumber: applicant.identificationNumber,
        address: applicant.address,
        name: applicant.name,
        representative: applicant.representative,
        phoneNumber: applicant.phoneNumber,
        payment: {
          method: applicant.payment.method,
          paymentNumber: paymentNumber
        }
      }
    );
  };

  return (
    <>
      <Form.Label>申出人</Form.Label>
      <div className="ms-lg-5">
        <IdentificationNumberForm
          identificationNumber={applicant.identificationNumber}
          isAgency={false}
          isRequired={true}
          onChange={updateIdentificationNumber}
        />
        <AddressForm
          address={applicant.address}
          isAgency={false}
          isRequired={true}
          onChange={updateAddress}
        />
        <NameForm
          name={applicant.name}
          isAgency={false}
          isRequired={true}
          onChange={updateName}
        />
        <RepresentativeForm
          representative={applicant.representative}
          name={applicant.name}
          isProcedureExecutor={true}
          onChange={updateRepresentative}
        />
        <PhoneNumberForm
          phoneNumber={applicant.phoneNumber}
          onChange={updatephoneNumber}
        />
        <PaymentForm
          paymentMethod={applicant.payment.method}
          setPaymentMethod={updatePaymentMethod}
          paymentNumber={applicant.payment.paymentNumber}
          setPaymentNumber={updatePaymentNumber}
        />
      </div>
    </>
  );
}

export default ApplicantForm;